import { Cohorts, Cohort, Attributes } from "../../types/Cohorts";
import { headers } from "./ApiHelpers";

const baseUrl = "/api/cohorts";

export const getCohorts = async (): Promise<Cohorts> => {
  const raw = await fetch(baseUrl, {
    headers: headers(),
  });
  const response: unknown = await raw.json();
  if (isCohorts(response)) return response;
  else throw new Error("Invalid response from server");
};

export const getCohort = async (id: string): Promise<Cohort> => {
  const raw = await fetch(`${baseUrl}/${id}`, {
    headers: headers(),
  });
  const response: unknown = await raw.json();
  if (isCohort(response)) return response;
  else throw new Error("Invalid response from server");
};

export const searchCohorts = async (query: string, size = 50, includeArchived = false): Promise<Cohort[]> => {
  const raw = await fetch(
    `${baseUrl}/search?size=${String(size)}&includeArchived=${String(includeArchived)}${query ? "&query=" : ""}${encodeURIComponent(
      query || ""
    )}`,
    {
      headers: headers(),
    }
  );
  const response: unknown = await raw.json();

  if (isCohortSearchResult(response)) {
    return response.cohorts;
  } else {
    throw new Error("Invalid response from server");
  }
};

export type updateCohortInput = {
  name: string;
  description?: string;
  groupIDsToAdd?: string[];
  groupIDsToRemove?: string[];
  attributesToAdd?: Attributes;
  attributesToRemove?: Attributes;
};
export interface updateCohortResp {
  id: string;
}
export const updateCohort = async (cohortId: string, cohort: updateCohortInput): Promise<updateCohortResp> => {
  if (!cohortId) throw new Error("Invalid cohort id");
  const raw = await fetch(`${baseUrl}/${cohortId}`, {
    method: "PATCH",
    headers: headers(),
    body: JSON.stringify(cohort),
  });
  if (raw.status !== 200) {
    let errorText = "Error updating cohort. Please try again later.";
    try {
      const errorResponse: unknown = await raw.json();
      if (
        typeof errorResponse === "object" &&
        errorResponse !== null &&
        typeof (errorResponse as Record<string, unknown>).error === "string" &&
        ((errorResponse as Record<string, unknown>).error as string).includes("duplicate key value violates unique constraint")
      ) {
        errorText = "Cohort name already in use";
      }
    } catch {}
    throw new Error(errorText);
  }

  const response: unknown = await raw.json();
  if (!(response !== null && typeof response === "object")) {
    throw new Error("Invalid response from server");
  }
  return response as updateCohortResp;
};

const isCohortSearchResult = (response: unknown): response is Cohorts => responseHasCohortsArray(response);

const isCohort = (cohort: unknown): cohort is Cohort => {
  return cohort !== null && typeof cohort === "object" && "id" in cohort && "name" in cohort;
};

const isCohorts = (response: unknown): response is Cohorts => {
  if (!responseHasCohortsArray(response)) return false;
  if (response.cohorts.length === 0) return true;

  return response.cohorts.some((cohort) => cohort !== null && typeof cohort === "object" && "id" in cohort && "name" in cohort);
};

const responseHasCohortsArray = (response: unknown): response is { cohorts: unknown[] } => {
  return response !== null && typeof response === "object" && "cohorts" in response && Array.isArray(response.cohorts);
};

export type CreateCohort = {
  name: string;
  description?: string;
};

export const createCohort = async (cohort: CreateCohort): Promise<string> => {
  const raw = await fetch(`${baseUrl}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name: cohort.name, description: cohort.description }),
  });
  if (raw.status !== 201) {
    let errorText = "Error creating cohort. Please try again later.";
    try {
      const errorResponse: unknown = await raw.json();
      if (
        typeof errorResponse === "object" &&
        errorResponse !== null &&
        typeof (errorResponse as Record<string, unknown>).error === "string" &&
        ((errorResponse as Record<string, unknown>).error as string).includes("duplicate key value violates unique constraint")
      ) {
        errorText = "Cohort name already in use.";
      }
    } catch {}
    throw new Error(errorText);
  }

  const response: unknown = await raw.json();
  if (!(response !== null && typeof response === "object" && "id" in response && typeof response.id === "string")) {
    throw new Error("Invalid response from server");
  }
  return response.id;
};

export const deleteCohort = async (cohortId: string): Promise<string> => {
  const raw = await fetch(`${baseUrl}/${cohortId}`, {
    method: "DELETE",
    headers: headers(),
  });
  if (raw.status !== 200) throw new Error("Invalid response from server");

  const response: unknown = await raw.json();
  if (!(response !== null && typeof response === "object" && "id" in response && typeof response.id === "string")) {
    throw new Error("Invalid response from server");
  }
  return response.id;
};

export const unarchiveCohort = async (cohortId: string): Promise<string> => {
  const raw = await fetch(`${baseUrl}/${cohortId}/unarchive`, {
    method: "PATCH",
    headers: headers(),
  });
  if (raw.status !== 200) throw new Error("Invalid response from server");

  const response: unknown = await raw.json();
  if (!(response !== null && typeof response === "object" && "id" in response && typeof response.id === "string")) {
    throw new Error("Invalid response from server");
  }
  return response.id;
};
